/**
 * Cross-platform path utilities
 * Handles both Windows (\) and Unix (/) path separators
 */

/** Detect the path separator used in a path */
export function detectSeparator(path: string): string {
  const backslashCount = (path.match(/\\/g) || []).length;
  const forwardSlashCount = (path.match(/\//g) || []).length;
  return backslashCount >= forwardSlashCount ? '\\' : '/';
}

/** Get the parent directory of a path */
export function getParentPath(path: string): string {
  if (!path || path === 'root') return 'root';

  const lastBackslash = path.lastIndexOf('\\');
  const lastForwardSlash = path.lastIndexOf('/');
  const lastSep = Math.max(lastBackslash, lastForwardSlash);

  if (lastSep === -1) return 'root';

  const parent = path.substring(0, lastSep);

  // Handle Windows drive root (e.g., "C:")
  if (parent.match(/^[A-Za-z]:$/)) {
    return parent + '\\';
  }

  return parent || 'root';
}

/** Get the file/folder name from a path */
export function getBaseName(path: string): string {
  if (!path || path === 'root') return '';

  const lastBackslash = path.lastIndexOf('\\');
  const lastForwardSlash = path.lastIndexOf('/');
  const lastSep = Math.max(lastBackslash, lastForwardSlash);

  return lastSep === -1 ? path : path.substring(lastSep + 1);
}

/** Join path segments using the appropriate separator */
export function joinPath(base: string, ...segments: string[]): string {
  if (!base || base === 'root') {
    return segments.join('/');
  }

  const sep = detectSeparator(base);
  let result = base;

  for (const segment of segments) {
    if (!segment) continue;
    // Remove leading separators from segment
    const cleanSegment = segment.replace(/^[/\\]+/, '');
    // Remove trailing separators from result
    result = result.replace(/[/\\]+$/, '');
    result = result + sep + cleanSegment;
  }

  return result;
}

/** Normalize path separators (convert all to the detected type) */
export function normalizePath(path: string): string {
  if (!path) return path;
  const sep = detectSeparator(path);
  const otherSep = sep === '\\' ? '/' : '\\';
  return path.split(otherSep).join(sep);
}
